import { type ReactNode, type HTMLAttributes } from 'react'
import { cn } from '@/shared/lib/utils'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Содержимое карточки
   */
  children: ReactNode
  /**
   * Вариант карточки
   * - `default`: стандартная карточка с тенью
   * - `outlined`: карточка с рамкой
   * - `elevated`: карточка с увеличенной тенью
   * @default 'default'
   */
  variant?: 'default' | 'outlined' | 'elevated'
  /**
   * Padding карточки
   * - `none`: без отступов
   * - `sm`: маленькие отступы (12px)
   * - `md`: средние отступы (16px)
   * - `lg`: большие отступы (24px)
   * @default 'md'
   */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /**
   * Кликабельна ли карточка
   * При включении добавляет hover эффекты и курсор pointer
   * @default false
   */
  clickable?: boolean
}

/**
 * UI компонент: Карточка
 * 
 * Базовый компонент карточки для группировки контента.
 * 
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>Заголовок</CardHeader>
 *   <CardContent>Контент карточки</CardContent>
 * </Card>
 * 
 * <Card clickable onClick={handleClick}>
 *   Кликабельная карточка
 * </Card>
 * ```
 */
export function Card({
  children,
  variant = 'default',
  padding = 'md',
  clickable = false,
  className,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        // Базовые стили - скругление 14-18px (используем 16px)
        'rounded-[16px] overflow-hidden',
        'transition-all duration-150',
        // Варианты - уровни поверхностей для тёмной темы
        {
          // Light theme
          'bg-white shadow-md': variant === 'default',
          'bg-white border border-silk': variant === 'outlined',
          'bg-white shadow-lg': variant === 'elevated',
          // Dark theme - уровни поверхностей
          'dark:bg-dark-card dark:shadow-md': variant === 'default',
          'dark:bg-dark-card dark:border dark:border-dark-border': variant === 'outlined',
          'dark:bg-dark-card dark:shadow-lg': variant === 'elevated',
        },
        // Padding
        {
          'p-0': padding === 'none',
          'p-3': padding === 'sm',
          'p-4': padding === 'md',
          'p-6': padding === 'lg',
        },
        // Кликабельность
        {
          'cursor-pointer hover:shadow-lg active:scale-[0.98]': clickable,
          'dark:hover:shadow-xl': clickable,
        },
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/**
 * Заголовок карточки
 */
export function CardHeader({ children, className, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn('px-4 pt-4 pb-2', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/**
 * Содержимое карточки
 */
export function CardContent({ children, className, ...props }: CardContentProps) {
  return (
    <div
      className={cn('px-4 py-2', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

/**
 * Подвал карточки
 */
export function CardFooter({ children, className, ...props }: CardFooterProps) {
  return (
    <div
      className={cn('px-4 pt-2 pb-4', className)}
      {...props}
    >
      {children}
    </div>
  )
}

